"use client";

import React, { createContext, useCallback, useContext } from "react";
import { useSession, SessionProvider } from "next-auth/react";
import { Session } from "next-auth";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { UserWithoutTeachers } from "@/types/users/userWithoutTeachers";
import { User } from "@/types/users/user";
import { QUERIES } from "@/config/queries";
import { SchoolWithoutTeachers } from "@/types/users/schoolWithoutTeachers";
import { Teacher } from "@/types/users/teacher";
import { City } from "@/types/users/city";

export interface AuthData {
    // Session data from next-auth
    session: Session | null;
    sessionStatus: "loading" | "authenticated" | "unauthenticated";

    // Additional user data from our backend
    user: User | null;
    userProfileCompleted: boolean;
    userLoading: boolean;
    userError: Error | null;
    currentTeacher: Teacher | null;
    currentSchool: SchoolWithoutTeachers | null;
    currentCity: City | null;
    refetchUser: () => void;
}

const AuthContext = createContext<AuthData | undefined>(undefined);

export const userProfileCompleted = (user: Partial<UserWithoutTeachers>) => {
    return (
        !!user?.first_name &&
        !!user?.last_name &&
        !!user?.main_subject &&
        !!user?.main_goal &&
        0 < (user?.classes || []).length
    );
};

function AuthProviderInner({ children }: { children: React.ReactNode }) {
    const { data: session, status: sessionStatus } = useSession();

    const queryClient = useQueryClient();

    const {
        data: user,
        isLoading: userLoading,
        error: userError,
    } = useQuery({
        ...QUERIES.currentUser.query,
        enabled:
            sessionStatus === "authenticated" &&
            !!session.idToken &&
            !!session.accessToken &&
            !!session,
    });

    const refetchUser = useCallback(() => {
        queryClient.refetchQueries({
            queryKey: QUERIES.currentUser.query.queryKey,
        });
    }, [queryClient]);

    const authData: AuthData = {
        session,
        sessionStatus,
        user: user || null,
        userProfileCompleted: !!user && userProfileCompleted(user),
        userLoading: userLoading || sessionStatus === "loading",
        userError: userError as Error | null,
        currentTeacher: user?.current_teacher || null,
        currentSchool: user?.current_teacher?.school || null,
        currentCity: user?.current_teacher?.school?.city || null,
        refetchUser,
    };

    return (
        <AuthContext.Provider value={authData}>{children}</AuthContext.Provider>
    );
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
    return (
        <SessionProvider>
            <AuthProviderInner>{children}</AuthProviderInner>
        </SessionProvider>
    );
}

export const useAuth = (): AuthData => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};
